import React, { useState, useEffect } from 'react';
import {
  X,
  User,
  Shield,
  Key,
  Calendar,
  Shirt,
  BookOpen,
  Sparkles,
  Check,
  AlertCircle,
  LogOut,
  UserPlus,
} from 'lucide-react';
import { User as UserType } from '../types';
import { api } from '../lib/api';

interface AccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: UserType | null;
  onLogout: () => void;
  onSwitchAccount: () => void;
}

export const AccountModal: React.FC<AccountModalProps> = ({
  isOpen,
  onClose,
  currentUser,
  onLogout,
  onSwitchAccount,
}) => {
  if (!isOpen || !currentUser) return null;

  const [stats, setStats] = useState<{
    clothes_count: number;
    outfits_count: number;
    body_profiles_count: number;
    created_at?: string;
  }>({
    clothes_count: 0,
    outfits_count: 0,
    body_profiles_count: 0,
  });

  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState<boolean>(false);
  const [passwordSuccess, setPasswordSuccess] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      api.auth
        .getProfile()
        .then((res) => {
          setStats({
            clothes_count: res.user.stats.clothes_count,
            outfits_count: res.user.stats.outfits_count,
            body_profiles_count: res.user.stats.body_profiles_count,
            created_at: res.user.created_at,
          });
        })
        .catch((err) => {
          console.warn('Failed to load profile stats', err);
        });
    }
  }, [isOpen]);

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');

    if (newPassword !== confirmNewPassword) {
      setErrorMsg('New passwords do not match.');
      return;
    }

    if (newPassword.length < 6) {
      setErrorMsg('New password must be at least 6 characters.');
      return;
    }

    setIsUpdatingPassword(true);

    try {
      await api.auth.updatePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });

      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setTimeout(() => setPasswordSuccess(false), 4000);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Failed to update password.');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md overflow-y-auto">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl my-auto animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/50">
          <div className="flex items-center space-x-2.5">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-brand-600 to-indigo-500 flex items-center justify-center text-white shadow-md">
              <User className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">Account Management</h3>
              <p className="text-xs text-slate-400">Manage your private closet &amp; credentials</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {errorMsg && (
          <div className="mx-6 mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2 text-xs text-red-300">
            <AlertCircle className="w-4 h-4 shrink-0 text-red-400" />
            <span>{errorMsg}</span>
          </div>
        )}

        <div className="p-6 space-y-6">
          {/* User Profile Card */}
          <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-brand-500/20 text-brand-300 border border-brand-500/30 flex items-center justify-center font-extrabold text-lg uppercase">
                {currentUser.username.substring(0, 2)}
              </div>
              <div>
                <span className="text-xs text-slate-400 font-medium block">Logged in as</span>
                <span className="text-base font-extrabold text-white">
                  @{currentUser.username}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={onSwitchAccount}
                className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold border border-slate-700 transition-colors flex items-center gap-1.5"
                title="Log into or register another account"
              >
                <UserPlus className="w-3.5 h-3.5 text-brand-400" />
                <span>Switch / Add Account</span>
              </button>
            </div>
          </div>

          {/* User Wardrobe Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-950/70 p-3 rounded-2xl border border-slate-800/80 text-center">
              <Shirt className="w-4 h-4 text-brand-400 mx-auto mb-1" />
              <span className="text-lg font-black text-white block">
                {stats.clothes_count}
              </span>
              <span className="text-[10px] text-slate-400 font-semibold uppercase">Clothes</span>
            </div>

            <div className="bg-slate-950/70 p-3 rounded-2xl border border-slate-800/80 text-center">
              <BookOpen className="w-4 h-4 text-indigo-400 mx-auto mb-1" />
              <span className="text-lg font-black text-white block">
                {stats.outfits_count}
              </span>
              <span className="text-[10px] text-slate-400 font-semibold uppercase">Outfits</span>
            </div>

            <div className="bg-slate-950/70 p-3 rounded-2xl border border-slate-800/80 text-center">
              <Sparkles className="w-4 h-4 text-amber-400 mx-auto mb-1" />
              <span className="text-lg font-black text-white block">
                {stats.body_profiles_count}
              </span>
              <span className="text-[10px] text-slate-400 font-semibold uppercase">Body Poses</span>
            </div>
          </div>

          {/* Change Password Form */}
          <form onSubmit={handleUpdatePassword} className="space-y-3 pt-2">
            <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
              <Key className="w-3.5 h-3.5 text-brand-400" />
              <span>Change Password</span>
            </h4>

            <div>
              <input
                type="password"
                required
                placeholder="Current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="password"
                required
                placeholder="New password (min 6 chars)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50"
              />
              <input
                type="password"
                required
                placeholder="Confirm new password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50"
              />
            </div>

            {passwordSuccess && (
              <p className="text-xs font-bold text-emerald-400 flex items-center gap-1">
                <Check className="w-4 h-4" />
                <span>Password updated successfully!</span>
              </p>
            )}

            <button
              type="submit"
              disabled={isUpdatingPassword || !currentPassword || !newPassword}
              className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold transition-colors disabled:opacity-50"
            >
              {isUpdatingPassword ? 'Updating...' : 'Update Password'}
            </button>
          </form>

          {/* Logout Action */}
          <div className="pt-4 border-t border-slate-800 flex items-center justify-between">
            <span className="text-xs text-slate-400">
              Host: closet.tiger-home.com
            </span>

            <button
              type="button"
              onClick={onLogout}
              className="inline-flex items-center space-x-1.5 px-4 py-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 text-xs font-semibold transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Log Out</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
